import React, { useRef, useEffect, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import type { Coordinates, GoogleMapsMarker } from '@/types';

interface GoogleMapProps {
  center?: Coordinates;
  zoom?: number;
  markers?: GoogleMapsMarker[];
  onMapClick?: (coordinates: Coordinates) => void;
  onMarkerClick?: (marker: GoogleMapsMarker, index: number) => void;
  height?: string;
  className?: string;
  showSearchBox?: boolean;
  onAddressSelect?: (address: string, coordinates: Coordinates) => void;
}

interface MapComponentProps extends GoogleMapProps {
  google: typeof window.google;
}

const MapComponent: React.FC<MapComponentProps> = ({
  center = { latitude: 41.9028, longitude: 12.4964 }, // Default to Rome
  zoom = 10,
  markers = [],
  onMapClick,
  onMarkerClick,
  height = '400px',
  className = '',
  showSearchBox = false,
  onAddressSelect,
  google
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [googleMarkers, setGoogleMarkers] = useState<google.maps.Marker[]>([]);

  // Keep the ref updated
  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !google) return;

    const mapInstance = new google.maps.Map(mapRef.current, {
      center: { lat: center.latitude, lng: center.longitude },
      zoom,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    });

    setMap(mapInstance);

    // Add click listener
    if (onMapClick) {
      mapInstance.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          onMapClick({
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng()
          });
        }
      });
    }

    return () => {
      // Cleanup listeners
      google.maps.event.clearInstanceListeners(mapInstance);
    };
  }, [google, center.latitude, center.longitude, zoom, onMapClick]);

  // Initialize search box
  useEffect(() => {
    if (!searchInputRef.current || !google || !google.maps.places || !showSearchBox || !map) return;

    const autocompleteInstance = new google.maps.places.Autocomplete(searchInputRef.current, {
      types: ['geocode'], // Changed from 'address' to 'geocode' for better coverage
      componentRestrictions: { country: 'it' }, // Restrict to Italy
      fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
      bounds: map.getBounds() || undefined // Use current map bounds for better suggestions
    });

    const handlePlaceChanged = () => {
      const place = autocompleteInstance.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        console.warn('No geometry found for place:', place);
        return;
      }

      const coordinates: Coordinates = {
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      };

      // Determine appropriate zoom level based on place type
      let zoomLevel = 16; // Default for addresses
      
      if (place.address_components) {
        const hasStreetNumber = place.address_components.some(
          component => component.types.includes('street_number')
        );
        const hasRoute = place.address_components.some(
          component => component.types.includes('route')
        );
        const hasLocality = place.address_components.some(
          component => component.types.includes('locality')
        );
        
        if (hasStreetNumber && hasRoute) {
          zoomLevel = 18; // Very specific address
        } else if (hasRoute) {
          zoomLevel = 16; // Street level
        } else if (hasLocality) {
          zoomLevel = 14; // City level
        } else {
          zoomLevel = 12; // Area level
        }
      }

      // Center map on selected location with smooth animation
      map.panTo(place.geometry.location);
      
      // Set zoom with a small delay to ensure proper rendering
      setTimeout(() => {
        map.setZoom(zoomLevel);
      }, 100);

      // If there's a viewport, fit the viewport for better positioning
      if (place.geometry.viewport) {
        map.fitBounds(place.geometry.viewport);
      }

      // Call callback if provided
      if (onAddressSelectRef.current) {
        onAddressSelectRef.current(place.formatted_address || place.name || '', coordinates);
      }
    };

    autocompleteInstance.addListener('place_changed', handlePlaceChanged);

    return () => {
      google.maps.event.clearInstanceListeners(autocompleteInstance);
    };
  }, [map, google, showSearchBox]); // Keep dependencies constant

  // Update markers when markers prop changes
  useEffect(() => {
    if (!map || !google) return;

    // Clear existing markers
    googleMarkers.forEach(marker => {
      marker.setMap(null);
    });

    // Add new markers
    const newMarkers = markers.map((markerData, index) => {
      const marker = new google.maps.Marker({
        position: { lat: markerData.position.latitude, lng: markerData.position.longitude },
        map,
        title: markerData.title,
      });

      // Add click listener
      if (onMarkerClick) {
        marker.addListener('click', () => {
          onMarkerClick(markerData, index);
        });
      }

      // Add info window if info is provided
      if (markerData.info) {
        const infoWindow = new google.maps.InfoWindow({
          content: markerData.info,
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      }

      return marker;
    });

    setGoogleMarkers(newMarkers);

    return () => {
      newMarkers.forEach(marker => {
        marker.setMap(null);
      });
    };
  }, [map, markers, onMarkerClick, google]);

  return (
    <div className="relative">
      {showSearchBox && (
        <div className="absolute top-2 left-2 z-10">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Cerca un indirizzo (es. Via Roma, Milano)..."
              className="w-80 px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      )}
      <div
        ref={mapRef}
        style={{ height }}
        className={`w-full rounded-lg ${className}`}
      />
    </div>
  );
};

const LoadingComponent: React.FC = () => (
  <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
    <div className="text-gray-600">Caricamento mappa...</div>
  </div>
);

const ErrorComponent: React.FC<{ status: Status }> = ({ status }) => (
  <div className="w-full h-96 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center">
    <div className="text-red-600">
      Errore nel caricamento della mappa: {status}
    </div>
  </div>
);

const GoogleMap: React.FC<GoogleMapProps> = (props) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <div className="w-full h-96 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-center">
        <div className="text-yellow-800">
          Google Maps API key non configurata. Aggiungi VITE_GOOGLE_MAPS_API_KEY al file .env
        </div>
      </div>
    );
  }

  const render = (status: Status) => {
    switch (status) {
      case Status.LOADING:
        return <LoadingComponent />;
      case Status.FAILURE:
        return <ErrorComponent status={status} />;
      case Status.SUCCESS:
        return <MapComponent {...props} google={window.google} />;
    }
  };

  return (
    <Wrapper
      apiKey={apiKey}
      render={render}
      libraries={['geometry', 'drawing', 'places']}
    />
  );
};

export default GoogleMap;